import React from "react";

import cn from "classnames";

import {
  ExclamationSVG,
  ClockFilledSVG,
  ClockSVG,
  CloseCircleSVG,
  DangerSVG,
  OpenClockSVG,
  TickCircleSVG,
  KycReviewSVG,
} from "assets/icons";
import { Tooltip } from "components/Tooltip";
import { PROCESS_STATUS } from "constants/process";
import { ProcessStatus } from "types/process";

import styles from "./StatusBadge.module.scss";

type Props = {
  status: ProcessStatus | "comingSoon" | "discontinued";
  customText?: React.ReactNode;
  pendingUpTo?: string;
  isAboutToExpired?: boolean;
  isTierBox?: boolean;
  isGlobalStatus?: boolean;
  isKYCStatus?: boolean;
  iconPosition?: "left" | "right";
  displayTooltips?: boolean;
  draftShouldUseDefaultTextColor?: boolean;
  isKyc?: boolean;
  isMultiline?: boolean;
  "data-cy"?: string;
};

export const StatusBadge = ({
  status,
  pendingUpTo,
  customText,
  isAboutToExpired,
  isTierBox,
  isGlobalStatus,
  isKYCStatus,
  iconPosition = "left",
  displayTooltips = false,
  draftShouldUseDefaultTextColor,
  isKyc,
  isMultiline,
  ...props
}: Props) => {
  const isIconPositionLeft = iconPosition === "left";

  if (isAboutToExpired) {
    return (
      <span {...props} className={cn(styles.statusBadge, styles.aboutToExpired, isMultiline && styles.multiline)}>
        {isIconPositionLeft && (
          <Tooltip shouldDisplay={displayTooltips} title='About to expire'>
            <ExclamationSVG />
          </Tooltip>
        )}{" "}
        {customText ?? "Issues found"} {!isIconPositionLeft && <ExclamationSVG />}
      </span>
    );
  }

  switch (status) {
    case PROCESS_STATUS.draft:
      return (
        <span {...props} className={cn(styles.statusBadge, { [styles[status]]: !draftShouldUseDefaultTextColor })}>
          {isIconPositionLeft && (
            <Tooltip shouldDisplay={displayTooltips} title='Unverified'>
              <TickCircleSVG />
            </Tooltip>
          )}{" "}
          {customText ??
            (isTierBox ? "Unverified" : isGlobalStatus || isKYCStatus ? "No issues found" : "No status yet")}{" "}
          {!isIconPositionLeft && <TickCircleSVG />}
        </span>
      );
    case PROCESS_STATUS.pending:
    case PROCESS_STATUS.submitted:
      return (
        <div>
          <span {...props} className={cn(styles.statusBadge, styles[status])}>
            {isIconPositionLeft && (
              <Tooltip shouldDisplay={displayTooltips} title='Pending'>
                {isKyc ? (
                  <KycReviewSVG
                    style={{
                      transform: "scale(1.5)",
                    }}
                  />
                ) : (
                  <ClockSVG />
                )}
              </Tooltip>
            )}
            {customText ?? <span>Pending</span>}
            {!isIconPositionLeft && (isKyc ? <KycReviewSVG /> : <ClockSVG />)}
            {pendingUpTo && isTierBox && <div className={styles.pendingUpToText}>{pendingUpTo}</div>}
          </span>

          {pendingUpTo && !isTierBox && <div className={styles.pendingUpToText}>{pendingUpTo}</div>}
        </div>
      );
    case PROCESS_STATUS.accepted:
      return (
        <span {...props} className={cn(styles.statusBadge, styles[status])}>
          {isIconPositionLeft && (
            <Tooltip shouldDisplay={displayTooltips} title='Verified'>
              <TickCircleSVG />
            </Tooltip>
          )}{" "}
          {customText ?? (isGlobalStatus || isKYCStatus ? "No issues found" : "Verified")}{" "}
          {!isIconPositionLeft && <TickCircleSVG />}
        </span>
      );
    case PROCESS_STATUS.rejected:
      return (
        <span {...props} className={cn(styles.statusBadge, styles[status])}>
          {isIconPositionLeft && (
            <Tooltip shouldDisplay={displayTooltips} title='Rejected'>
              <CloseCircleSVG />
            </Tooltip>
          )}{" "}
          {customText ?? (isGlobalStatus ? "Issues found" : <span>Rejected</span>)}{" "}
          {!isIconPositionLeft && <CloseCircleSVG />}
        </span>
      );
    case PROCESS_STATUS.outdated:
      return (
        <span {...props} className={cn(styles.statusBadge, styles[status])}>
          {isIconPositionLeft && (
            <Tooltip shouldDisplay={displayTooltips} title='Outdated'>
              <DangerSVG />
            </Tooltip>
          )}{" "}
          {customText ?? "Outdated"} {!isIconPositionLeft && <DangerSVG />}
        </span>
      );
    case PROCESS_STATUS.expired:
      return (
        <span {...props} className={cn(styles.statusBadge, styles[status])}>
          {isIconPositionLeft && (
            <Tooltip shouldDisplay={displayTooltips} title='Expired'>
              <ClockFilledSVG />
            </Tooltip>
          )}{" "}
          {customText ?? (isGlobalStatus ? "Issues found" : "Expired")} {!isIconPositionLeft && <ClockFilledSVG />}
        </span>
      );
    case "comingSoon":
      return (
        <span {...props} className={cn(styles.statusBadge, styles[status])}>
          {isIconPositionLeft && (
            <Tooltip shouldDisplay={displayTooltips} title='Coming soon'>
              <OpenClockSVG />
            </Tooltip>
          )}{" "}
          {customText ?? "Coming soon"} {!isIconPositionLeft && <OpenClockSVG />}
        </span>
      );
    case "discontinued":
      return (
        <span {...props} className={cn(styles.statusBadge, styles[status])}>
          {customText ?? "Discontinued"}
        </span>
      );
    default:
      return null;
  }
};
